import React, { useState, useRef, useEffect } from 'react';
import {
  Card,
  CardBody,
  Row,
} from 'reactstrap';
import { AlertCircle } from 'react-feather';
import { toast } from 'react-toastify';

import BulkCsvUpload from './BulkCsvUpload';
import BulkCsvColumnMapper from './BulkCsvColumnMapper';
import BulkPlanSelectionModal from './BulkPlanSelectionModal';
import { PITTI_CSV_COLUMNS } from './BulkCsvColumnMapperItem';

const BulkInviteTab = ({ setupPittiBrandRetailerAccountBulk, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [appendableColumns, setAppendableColumns] = useState([...PITTI_CSV_COLUMNS]);
  const [loading, setLoading] = useState(false);
  const [resultSummary, setResultSummary] = useState(null);


  // Plan selection modal
  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [parsedAccounts, setParsedAccounts] = useState([]);

  const handleTableColumnSelect = (selectedColumn, appendedValue) => {
    const updatedData = { ...tableData };
    const columnIndex = updatedData?.columns?.findIndex((c) => c?.Header === selectedColumn);

    if (columnIndex !== -1) {
      updatedData.columns[columnIndex] = {
        ...updatedData.columns[columnIndex],
        appendedValue,
      };
      setTableData(updatedData);
      setAppendableColumns((prev) => prev.filter((c) => c.id !== appendedValue));
    }
  };

  const handleTableColumnUnSelect = (selectedColumn) => {
    const updatedData = { ...tableData };
    const columnIndex = updatedData?.columns?.findIndex((c) => c?.Header === selectedColumn);

    if (columnIndex !== -1) {
      const appendedValue = updatedData.columns[columnIndex].appendedValue;
      updatedData.columns[columnIndex] = {
        Header: updatedData.columns[columnIndex].Header,
        accessor: updatedData.columns[columnIndex].accessor,
      };
      setTableData(updatedData);

      const appendableColumn = PITTI_CSV_COLUMNS.find((c) => c.id === appendedValue);
      if (appendableColumn) {
        setAppendableColumns((prev) => [...prev, appendableColumn]);
      }
    }
  };

  const handleTableDataRemove = () => {
    setAppendableColumns([...PITTI_CSV_COLUMNS]);
    setFile(null);
    setTableData(null);
    setStep(1);
    setResultSummary(null);
  };

  const validateData = () => {
    const unmappedRequired = appendableColumns.filter((c) => c.required);
    if (unmappedRequired.length > 0) {
      const names = unmappedRequired.map((c) => c.label).join(', ');
      toast.warning(`${names} ${unmappedRequired.length > 1 ? 'fields are' : 'field is'} required!`, { autoClose: 3000 });
      return false;
    }
    return true;
  };

  // Build accounts from CSV data + column mapping, then open modal
  const handleOpenPlanModal = () => {
    if (!validateData()) return;

    // Build column mapping: file header -> API field
    const columnMapping = {};
    tableData?.columns?.forEach((col) => {
      if (col.appendedValue) {
        columnMapping[col.Header] = col.appendedValue;
      }
    });

    // Transform row data into accounts
    const accounts = tableData?.data
      ?.filter((row) => {
        return Object.values(row).some((val) => val !== null && val !== undefined && val !== '');
      })
      ?.map((row) => {
        const account = {};

        Object.keys(row).forEach((header) => {
          const apiField = columnMapping[header];
          if (apiField) {
            let value = row[header];
            if (apiField === 'pitti_id' && value) {
              value = Number(value);
            }
            if (apiField === 'type' && value) {
              value = String(value).toLowerCase().trim();
            }
            if (apiField !== 'pitti_id' && value !== null && value !== undefined) {
              value = String(value).trim();
            }
            account[apiField] = value;
          }
        });

        return account;
      }) || [];

    if (accounts.length === 0) {
      toast.warning('No valid accounts found in the file.', { autoClose: 3000 });
      return;
    }

    setParsedAccounts(accounts);
    setPlanModalOpen(true);
  };

  // Called from the modal with accounts that now include pitti_plan_id per user
  const handleConfirmSubmit = async (accountsWithPlans) => {
    setLoading(true);
    setResultSummary(null);

    try {
      const result = await setupPittiBrandRetailerAccountBulk(accountsWithPlans);

      if (!result) {
        setPlanModalOpen(false);
        setResultSummary({
          type: 'error',
          message: 'No response received from the server. Please try again.',
        });
        return;
      }

      const statusCode = result?.statusCode;

      if (statusCode === 200) {
        // Synchronous processing complete
        const successful = result?.data?.successful || 0;
        const failed = result?.data?.failed || 0;
        let type = 'success';
        if (failed > 0 && successful > 0) {
          type = 'partial';
        } else if (failed > 0 && successful === 0) {
          type = 'error';
        }

        setPlanModalOpen(false);
        setResultSummary({
          type,
          total: result?.data?.total || accountsWithPlans.length,
          successful,
          failed,
          results: result?.data?.results || [],
          errors: result?.data?.errors || [],
        });
        if (failed === 0 && onSuccess) {
          onSuccess();
        }
      } else if (statusCode === 202) {
        // Async processing started
        setPlanModalOpen(false);
        setResultSummary({
          type: 'processing',
          total: result?.data?.total || accountsWithPlans.length,
          message: result?.message || 'Your data is being uploaded and processed in the background.',
        });
        if (onSuccess) onSuccess();
      } else if (statusCode === 400) {
        // Request-level validation error (e.g. empty array, bad format)
        setPlanModalOpen(false);
        setResultSummary({
          type: 'error',
          message: result?.message || 'Bad request. Please check your data and try again.',
        });
      } else if (statusCode === 500) {
        // Server error
        setPlanModalOpen(false);
        setResultSummary({
          type: 'error',
          message: result?.message || 'An unexpected server error occurred. Please try again later.',
        });
      } else {
        // Any other unexpected status
        setPlanModalOpen(false);
        setResultSummary({
          type: 'error',
          message: result?.message || 'An unexpected error occurred. Please try again.',
        });
      }
    } catch (error) {
      console.error('Error submitting bulk invites:', error);
      setPlanModalOpen(false);
      setResultSummary({
        type: 'error',
        message: error?.message || 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Row className="justify-content-center mt-3" style={{ width: '100%' }}>
      <Card className="shadow-sm">
        <CardBody className="p-4">
          <div className="mb-4">
            <h4
              className="card-title mb-1"
              style={{ fontWeight: '600', fontSize: '20px' }}
            >
              Bulk Pitti Invitation
            </h4>
            <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
              Upload a CSV file to send bulk Pitti invitations to brands and retailers
            </p>
          </div>


          {/* CSV Upload */}
          <BulkCsvUpload
            file={file}
            tableData={tableData}
            step={step}
            onFileAdd={setFile}
            onTableDataAdd={setTableData}
            onTableDataRemove={handleTableDataRemove}
            onStepChange={(s) => step !== s && s >= 0 && setStep(s)}
          />

          <hr className="my-3" />

          {/* Column Mapper */}
          <BulkCsvColumnMapper
            step={step}
            loading={loading}
            tableData={tableData}
            onSubmit={handleOpenPlanModal}
            appendableColumns={appendableColumns}
            setAppendableColumns={setAppendableColumns}
            onTableColumnSelect={handleTableColumnSelect}
            onTableColumnUnSelect={handleTableColumnUnSelect}
          />

          {/* Result Summary */}
          {resultSummary && (
            <div
              className="mb-4 p-3 rounded"
              style={{
                fontSize: '14px',
                ...(resultSummary.type === 'error' && { backgroundColor: '#fff5f5', border: '1px solid #fed7d7', color: '#9b2c2c' }),
                ...(resultSummary.type === 'partial' && { backgroundColor: '#fffff0', border: '1px solid #fefcbf', color: '#975a16' }),
                ...(resultSummary.type === 'processing' && { backgroundColor: '#ebf8ff', border: '1px solid #bee3f8', color: '#2a4365' }),
                ...(resultSummary.type === 'success' && { backgroundColor: '#f0fff4', border: '1px solid #c6f6d5', color: '#276749' }),
              }}
            >
              {/* Error — request-level (400, 500) or all accounts failed */}
              {resultSummary.type === 'error' && (
                <div>
                  <div className="d-flex align-items-center gap-2">
                    <AlertCircle size={18} className="flex-shrink-0" />
                    <strong>
                      {resultSummary.failed > 0
                        ? `All ${resultSummary.failed} account(s) failed to create`
                        : 'Request Failed'}
                    </strong>
                  </div>
                  {resultSummary.message && (
                    <div className="mt-2" style={{ fontSize: '13px' }}>
                      {resultSummary.message}
                    </div>
                  )}
                  {resultSummary.total > 0 && (
                    <div className="mt-1" style={{ fontSize: '13px' }}>
                      Total: {resultSummary.total} | Successful: {resultSummary.successful || 0} | Failed: {resultSummary.failed || 0}
                    </div>
                  )}
                  {resultSummary.errors?.length > 0 && (
                    <div className="mt-3">
                      <strong style={{ fontSize: '13px' }}>Error Details:</strong>
                      <div
                        className="mt-2"
                        style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                          borderRadius: '6px',
                          padding: '8px 12px',
                        }}
                      >
                        {resultSummary.errors.map((err, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-start gap-2 py-2"
                            style={{
                              fontSize: '12px',
                              borderBottom: idx < resultSummary.errors.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
                            }}
                          >
                            <AlertCircle size={14} className="flex-shrink-0 mt-1" />
                            <div>
                              <div>
                                <strong>Row {err.index + 1}</strong>
                                {err.account?.personal_name && <span> - {err.account.personal_name}</span>}
                                {err.account?.email && <span style={{ opacity: 0.7 }}> ({err.account.email})</span>}
                              </div>
                              <div style={{ marginTop: '2px' }}>
                                {err.pitti_id && <span className="me-2">Pitti ID: <strong>{err.pitti_id}</strong></span>}
                                {err.account?.type && <span className="me-2">Type: <strong>{err.account.type}</strong></span>}
                              </div>
                              <div style={{ marginTop: '4px', fontWeight: '500' }}>
                                {err.error}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Async processing (202) */}
              {resultSummary.type === 'processing' && (
                <div>
                  <strong>Processing {resultSummary.total} accounts in the background.</strong>
                  <div className="mt-1" style={{ fontSize: '13px' }}>
                    {resultSummary.message}
                  </div>
                  <div className="mt-2" style={{ fontSize: '12px', opacity: 0.8 }}>
                    Note: For batches larger than 10, processing happens asynchronously. Individual errors (if any) are logged on the server.
                  </div>
                </div>
              )}

              {/* Full success (200, 0 failed) */}
              {resultSummary.type === 'success' && (
                <div>
                  <strong>All {resultSummary.successful} account(s) created successfully!</strong>
                  <div className="mt-1" style={{ fontSize: '13px' }}>
                    Total: {resultSummary.total} | Successful: {resultSummary.successful} | Failed: {resultSummary.failed}
                  </div>
                </div>
              )}

              {/* Partial success (200, some failed) */}
              {resultSummary.type === 'partial' && (
                <div>
                  <strong>Bulk invite completed with some errors.</strong>
                  <div className="mt-1" style={{ fontSize: '13px' }}>
                    Total: {resultSummary.total} | Successful: {resultSummary.successful} | Failed: {resultSummary.failed}
                  </div>

                  {/* Per-account errors */}
                  {resultSummary.errors?.length > 0 && (
                    <div className="mt-3">
                      <strong style={{ fontSize: '13px' }}>Failed Accounts:</strong>
                      <div
                        className="mt-2"
                        style={{
                          maxHeight: '200px',
                          overflowY: 'auto',
                          backgroundColor: 'rgba(0,0,0,0.03)',
                          borderRadius: '6px',
                          padding: '8px 12px',
                        }}
                      >
                        {resultSummary.errors.map((err, idx) => (
                          <div
                            key={idx}
                            className="d-flex align-items-start gap-2 py-2"
                            style={{
                              fontSize: '12px',
                              borderBottom: idx < resultSummary.errors.length - 1 ? '1px solid rgba(0,0,0,0.08)' : 'none',
                            }}
                          >
                            <AlertCircle size={14} className="flex-shrink-0 mt-1" />
                            <div>
                              <div>
                                <strong>Row {err.index + 1}</strong>
                                {err.account?.personal_name && <span> - {err.account.personal_name}</span>}
                                {err.account?.email && <span style={{ opacity: 0.7 }}> ({err.account.email})</span>}
                              </div>
                              <div style={{ marginTop: '2px' }}>
                                {err.pitti_id && <span className="me-2">Pitti ID: <strong>{err.pitti_id}</strong></span>}
                                {err.account?.type && <span className="me-2">Type: <strong>{err.account.type}</strong></span>}
                              </div>
                              <div style={{ marginTop: '4px', fontWeight: '500' }}>
                                {err.error}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Successful accounts summary */}
                  {resultSummary.results?.length > 0 && (
                    <div className="mt-3">
                      <strong style={{ fontSize: '13px' }}>Successfully Created ({resultSummary.successful}):</strong>
                      <div className="mt-1" style={{ fontSize: '12px', opacity: 0.8 }}>
                        {resultSummary.results.map((r, idx) => (
                          <span key={idx}>
                            {r.account?.personal_name || r.account?.email || `Row ${r.index + 1}`}
                            {idx < resultSummary.results.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </CardBody>
      </Card>

      {/* Plan Selection Modal */}
      <BulkPlanSelectionModal
        open={planModalOpen}
        onClose={() => setPlanModalOpen(false)}
        accounts={parsedAccounts}
        loading={loading}
        onSubmit={handleConfirmSubmit}
      />
    </Row>
  );
};

export default BulkInviteTab;
